import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import TableImage from "../../shared/Table/TableImage";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { exportTableToCsv } from "../../utils/exportTableCsv";

export default function InvoiceTable({ heading, allInvoice, loading, history, search, setSearch }) {
    const [selectedRow, setSelectedRow] = useState([]);
    const [visible, setVisible] = useState(false);
 
    const statusOptions = [
        { label: "Active", value: "Active" },
        { label: "Expired", value: "Expired" },
        { label: "Cancelled", value: "Cancelled" },
        { label: "Paused", value: "Paused" },
        { label: "halted", value: "halted" },
        // Search Pro rows carry the payment status rather than a plan lifecycle.
        { label: "Success", value: "success" },
    ];
 
    const [filters, setFilters] = useState({
        time: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        amountPaidAt: { value: null, matchMode: FilterMatchMode.DATE_IS },
        status: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
 
    const processedInvoices = allInvoice.map((item) => ({
        ...item,
        amountPaidAt: item.amountPaidAt ? new Date(item.amountPaidAt) : null,
    }));
 
    const actions = (d) => (
        <div className="flex justify-content-around">
            <i className="pi pi-eye cursor-pointer text-dark" onClick={() => history(`/invoice/view/${d._id}`)}></i>
        </div>
    );
 
    const image = (r) => {
        const image = r?.user?.image;
        return <TableImage image={image} />;
    };
 
    const dateFilterTemplate = (options) => {
        return (
            <Calendar
                value={options.value ? new Date(options.value) : null}
                onChange={(e) => {
                    const selectedDate = e.value instanceof Date ? e.value : new Date(e.value);
                    options.filterCallback(selectedDate, options.index);
                }}
                dateFormat="mm/dd/yy"
                placeholder="mm/dd/yyyy"
                mask="99/99/9999"
                maxDate={new Date()}
            />
        );
    };
    const statusFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={statusOptions}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                placeholder="Select Status"
                className="p-column-filter"
                showClear
            />
        );
    };
    const formatDate = (date) => {
        if (!date) return "";
        date = new Date(date);
        return date.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };
 
    const exportAll = () => {
        exportTableToCsv("invoices.csv", processedInvoices, [
            { header: "Invoice Id", value: (row) => row?.invoice?.id || "-" },
            { header: "Type", value: (row) => (row?.invoiceType === "search_pro" ? "Search Pro" : "Plan") },
            { header: "Name", value: (row) => (row?.user ? `${row?.user?.firstName || ""} ${row?.user?.lastName || ""}`.trim() : "") },
            { header: "Plan", value: (row) => row?.planDetails?.name || "-" },
            { header: "Amount", value: "amountPaid" },
            { header: "Amount Paid", value: (row) => formatDate(row.amountPaidAt) },
            { header: "Status", value: "status" },
        ]);
    };

    const footerContent = (
        <div>
            <Button label="No" icon="pi pi-times" onClick={() => setVisible(false)} />
        </div>
    );
 
    return (
        <>
            <Dialog
                dismissableMask={true}
                visible={visible}
                showHeader={false}
                draggable={false}
                closable={false}
                style={{ width: "40vw" }}
                onHide={() => setVisible(false)}
                footer={footerContent}
            >
                <h5 className="mt-5 font-bold">Are you sure you want to delete this record?</h5>
                <p className="mt-5">User Id: {selectedRow?.[0]?.userId}</p>
            </Dialog>
 
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between mb-4">
                        <h5>{heading}</h5>
                        <div className="flex align-items-center">
                            {setSearch && (
                                <div className="p-input-icon-left mx-2">
                                    <i className="pi pi-search" />
                                    <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
                                </div>
                            )}
                            <Button
                                label="Export CSV"
                                icon="pi pi-download"
                                onClick={exportAll}
                                disabled={!processedInvoices.length}
                                severity="success"
                            />
                        </div>
                    </div>
                    <DataTable
                        paginator
                        rows={50}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        value={processedInvoices}
                        selection={selectedRow}
                        onSelectionChange={(e) => setSelectedRow(e.value)}
                        selectionMode="checkbox"
                        filters={filters}
                        onFilter={(e) => setFilters(e.filters)}
                        filterDisplay="menu"
                        loading={loading}
                        emptyMessage="No invoice found."
                        className="custom-datatable-header"
                        showGridlines
                    >
                        <Column field="invoice.id" body={(rowData) => rowData?.invoice?.id} header="Invoice Id" />
                        <Column
                            field="invoiceType"
                            header="Type"
                            body={(rowData) => (
                                <>
                                    {rowData?.invoiceType === "search_pro" ? "Search Pro" : "Plan"}
                                    {/* Account deactivated: still on the books, hidden from the user. */}
                                    {rowData?.accountHidden && (
                                        <span className="text-500" title="User deactivated their account"> (hidden)</span>
                                    )}
                                </>
                            )}
                            style={{ width: "140px" }}
                        />
                        <Column
                            field="name"
                            body={(rowData) => (rowData?.user ? `${rowData?.user?.firstName || ""} ${rowData?.user?.lastName || ""}` : "")}
                            header="Name"
                        />
                        <Column field="planDetails.name" header="Plan" body={(rowData) => rowData?.planDetails?.name || "-"} />
                        <Column field="amountPaid" header="Amount" />
                        <Column
                            header="Amount Paid"
                            field="amountPaidAt"
                            dataType="date"
                            filterMatchMode="dateIs"
                            showFilterMatchModes={true}
                            filter
                            filterElement={dateFilterTemplate}
                            filterType="date"
                            body={(rowData) => formatDate(rowData.amountPaidAt)}
                            style={{ width: "150px" }}
                        />
                        <Column body={image} header="Image" style={{ width: "80px" }} />
                        <Column
                            field="status"
                            header="Status"
                            filter
                            filterElement={statusFilterTemplate}
                            showFilterMatchModes={false}
                            style={{ width: "150px" }}
                        />
                        <Column body={actions} style={{ width: "80px" }} header="Action" />
                    </DataTable>
                </div>
            </div>
        </>
    );
}
 